import React from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import BuyCoinsBarsServices from "../../services/buy-coins-bars.service";
import { userStore } from "store/UserStore";
import { toast } from "react-toastify";
import { indianStates } from "../../data/const";
import "../../styles/BillingAddressModal.css";

const BillingAddressModal = ({ show, onClose, customerDetails, onAddressAdded }) => {
  const [customerId] = userStore((state: any) => [state.customerId]);

  const isDisabled = (field: string) => {
    const val = customerDetails?.[field];
    return val !== null && val !== undefined && val !== "";
  };

  const validationSchema = Yup.object({
    address1: Yup.string().required("Address Line 1 is required"),
    city: Yup.string().required("City is required"),
    pinCode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Pincode is required"),
    state: Yup.string().required("State is required"),
  });

  const formik = useFormik({
    initialValues: {
      displayName: customerDetails?.displayName || "",
      email: customerDetails?.email || "",
      mobile: customerDetails?.mobile?.replace("+91", "") || "",
      pinCode: customerDetails?.pinCode || "",
      address1: customerDetails?.address1 || "",
      address2: customerDetails?.address2 || "",
      city: customerDetails?.city || "",
      state: customerDetails?.state || "Maharashtra",
      country: "India",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload: any = { customerId };
        Object.keys(values).forEach((key) => {
          if (!isDisabled(key)) {
            payload[key] = values[key];
          }
        });

        const response = await BuyCoinsBarsServices.updateBillingAddress(payload);
        if (response?.status === 200) {
          toast.success("Billing address saved successfully!");
          if (onAddressAdded) await onAddressAdded();
          onClose();
        } else {
          toast.error("Failed to save billing address");
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Billing Address</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={6}>
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="displayName"
                value={formik.values.displayName}
                disabled
              />
            </Col>
            <Col xs={6}>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formik.values.email}
                disabled
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={6}>
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                type="text"
                name="mobile"
                value={formik.values.mobile}
                disabled
              />
            </Col>
            <Col xs={6}>
              <Form.Label>Pincode</Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("pinCode")}
                disabled={isDisabled("pinCode")}
              />
              {formik.touched.pinCode &&  typeof formik.errors.pinCode === "string" && (
                <div className="text-danger small">{formik.errors.pinCode}</div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Label>Address Line 1</Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("address1")}
                disabled={isDisabled("address1")}
              />
              {formik.touched.address1 && typeof formik.errors.address1 === "string" && (
                <div className="text-danger small">{formik.errors.address1}</div>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("address2")}
                disabled={isDisabled("address2")}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={6}>
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("city")}
                disabled={isDisabled("city")}
              />
              {formik.touched.city && typeof formik.errors.city === "string" && (
                <div className="text-danger small">{formik.errors.city}</div>
              )}
            </Col>
            <Col xs={6}>
              <Form.Label>State</Form.Label>
              <Form.Select
                {...formik.getFieldProps("state")}
                disabled={isDisabled("state")}
              >
                {indianStates.map((state, idx) => (
                  <option key={idx} value={state}>
                    {state}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={6}>
              <Form.Label>Country</Form.Label>
              <Form.Control type="text" value="India" disabled />
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Address</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default BillingAddressModal;
